import { useEffect, useRef, useState } from 'react';
import { COUNTRY_CODES } from '@goyone/shared';
import { Icon } from './Icon';

/**
 * منتقي مفتاح الدولة بالعلم وحده.
 *
 * القوائم الأصلية لا تعرض صورًا في خياراتها، وويندوز لا يرسم أعلام
 * الإيموجي أصلًا — فالأعلام هنا SVG مبسّطة مرسومة يدويًّا: الألوان
 * والتقسيم الصحيحان بلا شعارات دقيقة، تكفي لتمييز الدولة بنظرة
 * والاسم الكامل في القائمة المفتوحة والتلميح.
 */
export function CountrySelect({
  value,
  onChange,
  ariaLabel,
}: {
  /** المفتاح بلا زائد — '' تعني بلا مفتاح. */
  value: string;
  onChange: (code: string) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const current = COUNTRY_CODES.find((c) => c.code === value);

  return (
    <div className="ctry" ref={ref}>
      <button
        type="button"
        className="ctry-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel}
        aria-expanded={open}
        title={current ? `${current.label} +${current.code}` : 'بدون مفتاح دولة'}
      >
        {current ? <Flag code={current.code} /> : <span className="tiny mute">∅</span>}
        <Icon name="chevronDown" size={11} />
      </button>

      {open && (
        <div className="ctry-panel" role="listbox">
          {COUNTRY_CODES.map((c) => (
            <button
              key={c.code}
              type="button"
              role="option"
              aria-selected={c.code === value}
              className={`ctry-row${c.code === value ? ' is-on' : ''}`}
              onClick={() => {
                onChange(c.code);
                setOpen(false);
              }}
            >
              <Flag code={c.code} />
              <span className="num tiny" dir="ltr">
                +{c.code}
              </span>
              <span className="small">{c.label}</span>
            </button>
          ))}
          <button
            type="button"
            className={`ctry-row${value === '' ? ' is-on' : ''}`}
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
          >
            <span className="ctry-flag ctry-flag-none">∅</span>
            <span className="small">بدون مفتاح — الرقم كما هو</span>
          </button>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   الأعلام — SVG مبسّطة (18×12)
   ========================================================================== */

/** شريط أفقي بثلاثة ألوان. */
function H3({ top, mid, bot }: { top: string; mid: string; bot: string }) {
  return (
    <>
      <rect width="18" height="4" fill={top} />
      <rect y="4" width="18" height="4" fill={mid} />
      <rect y="8" width="18" height="4" fill={bot} />
    </>
  );
}

/** مثلث عند السارية (يسار اللوحة — الأعلام تُرسم بالاتجاه العالمي). */
function Tri({ color }: { color: string }) {
  return <path d="M0 0 L7 6 L0 12 Z" fill={color} />;
}

export function Flag({ code, size = 18 }: { code: string; size?: number }) {
  return (
    <svg
      className="ctry-flag"
      viewBox="0 0 18 12"
      width={size}
      height={(size * 2) / 3}
      aria-hidden="true"
    >
      <FlagArt code={code} />
      <rect width="18" height="12" fill="none" stroke="rgba(0,0,0,.18)" strokeWidth="0.5" />
    </svg>
  );
}

function FlagArt({ code }: { code: string }) {
  switch (code) {
    case '20': // مصر
      return (
        <>
          <H3 top="#CE1126" mid="#fff" bot="#000" />
          <circle cx="9" cy="6" r="1.3" fill="#C09300" />
        </>
      );
    case '966': // السعودية
      return (
        <>
          <rect width="18" height="12" fill="#165d31" />
          <rect x="4" y="7.5" width="10" height="1" fill="#fff" />
          <rect x="4" y="4" width="10" height="1.6" fill="#fff" opacity="0.9" />
        </>
      );
    case '971': // الإمارات
      return (
        <>
          <H3 top="#00843D" mid="#fff" bot="#000" />
          <rect width="5" height="12" fill="#CE1126" />
        </>
      );
    case '965': // الكويت
      return (
        <>
          <H3 top="#007A3D" mid="#fff" bot="#CE1126" />
          <path d="M0 0 L5 4 L5 8 L0 12 Z" fill="#000" />
        </>
      );
    case '974': // قطر
      return (
        <>
          <rect width="18" height="12" fill="#8A1538" />
          <rect width="5" height="12" fill="#fff" />
          <path
            d="M5 0 L7 1 L5 2 L7 3 L5 4 L7 5 L5 6 L7 7 L5 8 L7 9 L5 10 L7 11 L5 12 Z"
            fill="#fff"
          />
        </>
      );
    case '973': // البحرين
      return (
        <>
          <rect width="18" height="12" fill="#CE1126" />
          <rect width="5" height="12" fill="#fff" />
          <path
            d="M5 0 L8 1.2 L5 2.4 L8 3.6 L5 4.8 L8 6 L5 7.2 L8 8.4 L5 9.6 L8 10.8 L5 12 Z"
            fill="#fff"
          />
        </>
      );
    case '968': // عُمان
      return (
        <>
          <H3 top="#fff" mid="#DB161B" bot="#008000" />
          <rect width="5" height="12" fill="#DB161B" />
        </>
      );
    case '962': // الأردن
      return (
        <>
          <H3 top="#000" mid="#fff" bot="#007A3D" />
          <Tri color="#CE1126" />
        </>
      );
    case '961': // لبنان
      return (
        <>
          <rect width="18" height="12" fill="#ED1C24" />
          <rect y="3" width="18" height="6" fill="#fff" />
          <path d="M9 3.6 L11 7.6 L7 7.6 Z" fill="#00A651" />
        </>
      );
    case '964': // العراق
      return (
        <>
          <H3 top="#CE1126" mid="#fff" bot="#000" />
          <text x="9" y="7.4" textAnchor="middle" fontSize="3.4" fill="#007A3D">
            الله
          </text>
        </>
      );
    case '963': // سوريا
      return (
        <>
          <H3 top="#CE1126" mid="#fff" bot="#000" />
          <circle cx="6" cy="6" r="0.9" fill="#007A3D" />
          <circle cx="12" cy="6" r="0.9" fill="#007A3D" />
        </>
      );
    case '970': // فلسطين
      return (
        <>
          <H3 top="#000" mid="#fff" bot="#007A3D" />
          <Tri color="#CE1126" />
        </>
      );
    case '212': // المغرب
      return (
        <>
          <rect width="18" height="12" fill="#C1272D" />
          <path
            d="M9 3.2 L10 6.2 L13 6.2 L10.6 8 L11.5 11 L9 9.2 L6.5 11 L7.4 8 L5 6.2 L8 6.2 Z"
            fill="none"
            stroke="#006233"
            strokeWidth="0.7"
          />
        </>
      );
    case '213': // الجزائر
      return (
        <>
          <rect width="9" height="12" fill="#006233" />
          <rect x="9" width="9" height="12" fill="#fff" />
          <circle cx="9.5" cy="6" r="2.6" fill="#D21034" />
          <circle cx="10.4" cy="6" r="2.1" fill="#fff" />
          <path d="M10.2 4.8 L11 6 L10.2 7.2 L11.6 6.8 L12.4 6 L11.6 5.2 Z" fill="#D21034" />
        </>
      );
    case '216': // تونس
      return (
        <>
          <rect width="18" height="12" fill="#E70013" />
          <circle cx="9" cy="6" r="3.2" fill="#fff" />
          <circle cx="9.4" cy="6" r="2.4" fill="#E70013" />
          <circle cx="10.1" cy="6" r="1.9" fill="#fff" opacity="0" />
          <circle cx="10" cy="6" r="1.8" fill="#fff" />
          <circle cx="9.2" cy="6" r="0.8" fill="#E70013" />
        </>
      );
    case '218': // ليبيا
      return (
        <>
          <rect width="18" height="3" fill="#E70013" />
          <rect y="3" width="18" height="6" fill="#000" />
          <rect y="9" width="18" height="3" fill="#239E46" />
          <circle cx="8.4" cy="6" r="1.7" fill="#fff" />
          <circle cx="9" cy="6" r="1.4" fill="#000" />
          <path d="M10 5.2 L10.5 6 L10 6.8 L11.1 6.5 L11.6 6 L11.1 5.5 Z" fill="#fff" />
        </>
      );
    case '249': // السودان
      return (
        <>
          <H3 top="#D21034" mid="#fff" bot="#000" />
          <Tri color="#007229" />
        </>
      );
    case '967': // اليمن
      return <H3 top="#CE1126" mid="#fff" bot="#000" />;
    case '90': // تركيا
      return (
        <>
          <rect width="18" height="12" fill="#E30A17" />
          <circle cx="7" cy="6" r="2.8" fill="#fff" />
          <circle cx="7.8" cy="6" r="2.2" fill="#E30A17" />
          <path d="M10.6 5 L11 6 L10.6 7 L11.9 6.6 L12.5 6 L11.9 5.4 Z" fill="#fff" />
        </>
      );
    case '44': // المملكة المتحدة
      return (
        <>
          <rect width="18" height="12" fill="#012169" />
          <path d="M0 0 L18 12 M18 0 L0 12" stroke="#fff" strokeWidth="2.2" />
          <path d="M0 0 L18 12 M18 0 L0 12" stroke="#C8102E" strokeWidth="1" />
          <rect x="7.2" width="3.6" height="12" fill="#fff" />
          <rect y="4.2" width="18" height="3.6" fill="#fff" />
          <rect x="8" width="2" height="12" fill="#C8102E" />
          <rect y="5" width="18" height="2" fill="#C8102E" />
        </>
      );
    case '1': // أمريكا وكندا
      return (
        <>
          <rect width="18" height="12" fill="#fff" />
          {[0, 2, 4, 6, 8, 10].map((y) => (
            <rect key={y} y={y} width="18" height="1" fill="#B22234" />
          ))}
          <rect width="8" height="6" fill="#3C3B6E" />
        </>
      );
    default:
      return <rect width="18" height="12" fill="var(--line)" />;
  }
}
